<script setup>
import { usePhonePaste } from '@/composables/phonePaste'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useNotify } from '@/composables/notify'
import { useDetectDevice } from '@/composables/device'
import i18n from '@/plugins/i18n'
import * as agentsApi from '@/apis/agents.api'
import { AGENTS_CONSTANTS } from '@/constants/agents.constants'

// consente di incollare il numero: la maschera del campo tel scarta tutto cio che non e cifra
const { onPhonePaste: onAgentPhonePaste } = usePhonePaste((v) => { form.associatedPhoneNumber = v })

const router = useRouter()
const route = useRoute()
const { notifyError, notifySuccess } = useNotify()
const { isMobile } = useDetectDevice()
const t = i18n.global.t
const locale = i18n.global.locale

const props = defineProps({
  itemId: { type: [String, Number], required: false },
  fullScreen: { type: Boolean, default: false },
  title: { type: String, default: 'scheduled.create.title' },
  mode: { type: String, required: true, validator: value => ['edit', 'create'].includes(value) },
  languages: { type: Array, default: () => [] },
  topics: { type: Array, default: () => [] },
  maxPhoneNumberChanges: { type: Number, default: 0 }
})

const emit = defineEmits(['update:modelValue', 'saved'])
const loading = ref(true)
const agentForm = ref(null)
const numberChangesCounter = ref(0)

const form = reactive({
  name: '',
  language: '',
  topics: [],
  responseRate: AGENTS_CONSTANTS.RESPONSE_RATE_DEFAULT, // default medium
  textLength: AGENTS_CONSTANTS.TEXT_LENGTH_DEFAULT,
  emojiPercentage: AGENTS_CONSTANTS.EMOJI_PERCENTAGE_DEFAULT,
  startOperatingTime: '',
  finishOperatingTime: '',
  associatedPhoneNumber: '' // only for create mode
})

const initialData = reactive({})
const show = computed({
  get: () => props.mode === 'edit' ? !!route.query.edit : !!route.query.create,
  set: value => { if (!value) closeDialog() }
})

const hasChanges = computed(() => {
  if (props.mode === 'create') return true
  return Object.keys(initialData).some(key => form[key] !== initialData[key])
})

const isPhoneInputDisabled = computed(() => {
  return props.mode === 'edit' && numberChangesCounter.value >= props.maxPhoneNumberChanges
})

const usedChanges = computed(() => {
  if (props.mode !== 'edit') return 0
  return numberChangesCounter.value
})

const resetForm = (data = {}) => {
  Object.assign(form, {
    name: '',
    language: '',
    topics: [],
    responseRate: AGENTS_CONSTANTS.RESPONSE_RATE_DEFAULT,
    textLength: AGENTS_CONSTANTS.TEXT_LENGTH_DEFAULT,
    emojiPercentage: AGENTS_CONSTANTS.EMOJI_PERCENTAGE_DEFAULT,
    startOperatingTime: '',
    finishOperatingTime: '',
    associatedPhoneNumber: '',
    ...data
  })
}

watch(show, (isOpen) => {
  if (!isOpen) {
    resetForm()
  }
})

const fetchAgentDetailAsync = async() => {
  if (props.mode !== 'edit') return
  if (!props.itemId) return

  try {
    const { data } = await agentsApi.fetchAgentDetailAsync(props.itemId)
    const mapped = {
      name: data.name,
      language: data.language,
      topics: data.topics,
      responseRate: convertRateFromDb(data.response_rate, AGENTS_CONSTANTS.RESPONSE_RATE_MAP),
      textLength: convertRateFromDb(data.text_length, AGENTS_CONSTANTS.TEXT_LENGTH_MAP),
      emojiPercentage: convertRateFromDb(data.emoji_percentage, AGENTS_CONSTANTS.EMOJI_PERCENTAGE_MAP),
      startOperatingTime: data.start_operating_time,
      finishOperatingTime: data.finish_operating_time,
      associatedPhoneNumber: data.associated_phone_number.number,
    }
    resetForm(mapped)
    Object.assign(initialData, mapped)

    numberChangesCounter.value = data.number_changes_counter || 0
  } catch (error) {
    notifyError(t('agents.notifications.errorFetchingDetails'), error)
  }
}

const save = async() => {
  loading.value = true

  const isValid = await agentForm.value.validate()
  if (!isValid) {
    notifyError(t('form.notifications.fixErrors'))
    loading.value = false
    return
  }
  const payload = {
    associated_phone_number: form.associatedPhoneNumber,
    name: form.name,
    language: form.language,
    topics: form.topics,
    response_rate: convertRateToDb(form.responseRate, AGENTS_CONSTANTS.RESPONSE_RATE_MAP, AGENTS_CONSTANTS.RESPONSE_RATE_DEFAULT),
    text_length: convertRateToDb(form.textLength, AGENTS_CONSTANTS.TEXT_LENGTH_MAP, AGENTS_CONSTANTS.TEXT_LENGTH_DEFAULT),
    emoji_percentage: convertRateToDb(form.emojiPercentage, AGENTS_CONSTANTS.EMOJI_PERCENTAGE_MAP, AGENTS_CONSTANTS.EMOJI_PERCENTAGE_DEFAULT),
    start_operating_time: form.startOperatingTime,
    finish_operating_time: form.finishOperatingTime
  }
  try {
    if (props.mode === 'edit') {
      await agentsApi.updateAgentAsync(props.itemId, payload)
    } else {
      await agentsApi.createAgentAsync(payload)
    }
    notifySuccess(t('agents.notifications.savingSuccess'))
    closeDialog()
  } catch (error) {
    if (error.status === 500) {
      notifyError(t('error.serverError'))
    } else {
      notifyError(t(error?.response?.data[0]))
    }
  } finally {
    emit('saved')
    loading.value = false
  }
}

const closeDialog = () => {
  const query = { ...route.query }
  if (props.mode === 'edit') delete query.edit
  if (props.mode === 'create') delete query.create
  router.replace({ query })
}

onMounted(async() => {
  if (props.mode === 'edit' && !props.itemId) {
    loading.value = false
    return
  }

  await Promise.all([fetchAgentDetailAsync()])

  loading.value = false
})

const convertRateFromDb = (savedValue, mapToSearch) => {
  const entry = Object.entries(mapToSearch).find(([, v]) => v.value === savedValue)
  return entry ? Number(entry[0]) : 2
}

const convertRateToDb = (rate, mapToSearch, defaultKey) => mapToSearch[rate].value ?? mapToSearch[defaultKey].value
watch(() => props.itemId, fetchAgentDetailAsync)

</script>

<template>
  <q-dialog
    v-model="show"
    :maximized="fullScreen || isMobile"
  >
    <q-card
      v-touch-swipe.mouse.right="closeDialog"
      class="dialog-card"
    >
      <!-- Header -->
      <q-card-section class="dialog-header bg-primary text-white row items-center">
        <div class="col">
          <div class="text-h6">
            {{ $t(props.title) }}
          </div>
        </div>
      </q-card-section>

      <q-separator />

      <!-- Body -->
      <div class="dialog-body q-pa-md">
        <q-form
          ref="agentForm"
          class="q-gutter-md"
          @submit.prevent="save"
        >
          <div
            v-if="mode === 'edit'"
            :class="usedChanges === props.maxPhoneNumberChanges ? 'text-negative' : 'text-grey-7'"
          >
            {{ $t('agents.list.changesRemaining') }}: {{ usedChanges }} / {{ props.maxPhoneNumberChanges }}
          </div>
          <q-tel-input
            v-model="form.associatedPhoneNumber"
            @paste.capture="onAgentPhonePaste"
            :locales="locale"
            :disable="isPhoneInputDisabled"
          />

          <!-- Name / Language / Topics -->
          <q-input
            v-model="form.name"
            :label="$t('agents.create.name')"
            :rules="[val => !!val || $t('form.field.required')]"
            required
          />

          <q-select
            v-model="form.language"
            :options="props.languages.map(l => ({ label: l.name, value: l.id }))"
            emit-value
            map-options
            :label="$t('common.language')"
            :rules="[val => !!val || $t('form.field.required')]"
            required
          />

          <q-select
            v-model="form.topics"
            :options="props.topics.map(t => ({ label: t.title, value: t.id }))"
            multiple
            use-chips
            emit-value
            map-options
            :label="$t('agents.create.topics')"
            :rules="[val => val && val.length > 0 || $t('form.field.required')]"
            required
          />

          <!-- Response Rate -->
          <div class="form-section q-px-md">
            <div class="text-subtitle2 q-mb-xs">
              {{ $t('agents.create.responseRate') }}
            </div>
            <q-slider
              v-model.number="form.responseRate"
              :min="1"
              :max="Object.keys(AGENTS_CONSTANTS.RESPONSE_RATE_MAP).length"
              :step="1"
              color="primary"
              :marker-labels="Object.values(AGENTS_CONSTANTS.RESPONSE_RATE_MAP).map((key, index) => ({
                value: index + 1,
                label: i18n.global.t(key.label)
              }))"
            />
          </div>

          <!-- Text Length -->
          <div class="form-section q-px-md">
            <div class="text-subtitle2 q-mb-xs">
              {{ $t('agents.create.textLength') }}
            </div>
            <q-slider
              v-model.number="form.textLength"
              :min="1"
              :max="Object.keys(AGENTS_CONSTANTS.TEXT_LENGTH_MAP).length"
              :step="1"
              color="primary"
              :marker-labels="Object.values(AGENTS_CONSTANTS.TEXT_LENGTH_MAP).map((key, index) => ({
                value: index + 1,
                label: i18n.global.t(key.label)
              }))"
            />
          </div>

          <!-- Emoji Percentage -->
          <div class="form-section q-px-md">
            <div class="text-subtitle2 q-mb-xs">
              {{ $t('agents.create.emojiPercentage') }}
            </div>
            <q-slider
              v-model.number="form.emojiPercentage"
              :min="1"
              :max="Object.keys(AGENTS_CONSTANTS.EMOJI_PERCENTAGE_MAP).length"
              :step="1"
              color="primary"
              :marker-labels="Object.values(AGENTS_CONSTANTS.EMOJI_PERCENTAGE_MAP).map((key, index) => ({
                value: index + 1,
                label: i18n.global.t(key.label)
              }))"
            />
          </div>

          <q-input
            v-model="form.startOperatingTime"
            type="time"
            :label="$t('agents.create.startOperatingTime')"
            :rules="[val => !!val || $t('form.field.required')]"
          />

          <q-input
            v-model="form.finishOperatingTime"
            type="time"
            :label="$t('agents.create.finishOperatingTime')"
            :rules="[val => !!val || $t('form.field.required')]"
          />
        </q-form>
      </div>

      <!-- Footer -->
      <q-card-actions class="dialog-footer q-gutter-sm bg-white">
        <q-btn
          :label="$t('common.cancel')"
          flat
          :loading="loading"
          @click="closeDialog"
        />
        <q-btn
          :label="$t('common.save')"
          color="primary"
          :loading="loading"
          :disabled="!hasChanges || loading"
          @click="save"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

